/**
 * Editor context attached to messages for the Assistant.
 *
 * The AI panel shows what is selected as chips and prefixes the outgoing
 * message with a short, machine-readable block so the Assistant knows which
 * file and which entity/part "this" refers to.
 */

export interface ContextChip {
  readonly kind: 'scene' | 'entity' | 'model' | 'part' | 'file' | 'play' | 'sound' | 'analysis';
  readonly label: string;
  /** What goes into the context block. */
  readonly value: string;
}

export interface EditorContextInput {
  tab: string;
  scenePath?: string | null;
  selectedEntities?: readonly { id: string; name?: string | null }[];
  modelPath?: string | null;
  selectedPart?: string | null;
  filePath?: string | null;
  playing?: boolean;
  /** Sound tab: open `*.sound.json`, its parsed document and selection. */
  soundPath?: string | null;
  soundDoc?: unknown;
  /** Selected element as an edit path (`layers[2]`, `tracks.Piano`, `patterns.verse`). */
  soundSelection?: string | null;
  /** Last render analysis (the `analysis` JSON of the render route / SoundRenderer). */
  soundAnalysis?: unknown;
}

export function contextChips(input: EditorContextInput): ContextChip[] {
  const chips: ContextChip[] = [];
  switch (input.tab) {
    case 'scene':
    case 'play':
      if (input.scenePath) {
        chips.push({ kind: 'scene', label: basename(input.scenePath), value: `scene ${input.scenePath}` });
      }
      for (const entity of (input.selectedEntities ?? []).slice(0, 4)) {
        chips.push({
          kind: 'entity',
          label: entity.name || entity.id,
          value: `selected entity '${entity.id}'`,
        });
      }
      if (input.tab === 'play' && input.playing) {
        chips.push({ kind: 'play', label: 'Play mode', value: 'game is running in Play mode' });
      }
      break;
    case 'model':
      if (input.modelPath) {
        chips.push({ kind: 'model', label: basename(input.modelPath), value: `model ${input.modelPath}` });
      }
      if (input.selectedPart) {
        chips.push({ kind: 'part', label: input.selectedPart, value: `selected part '${input.selectedPart}'` });
      }
      break;
    case 'sound':
      if (input.soundPath) {
        const outline = input.soundDoc ? soundDocSummary(input.soundDoc) : '';
        chips.push({
          kind: 'sound',
          label: basename(input.soundPath),
          value: `sound ${input.soundPath}${outline ? ` (${outline})` : ''}`,
        });
      }
      if (input.soundSelection) {
        chips.push({
          kind: 'part',
          label: input.soundSelection,
          value: `selected sound element '${input.soundSelection}'`,
        });
      }
      if (input.soundAnalysis) {
        const line = soundAnalysisLine(input.soundAnalysis);
        if (line) chips.push({ kind: 'analysis', label: 'Last render', value: `last render: ${line}` });
      }
      break;
    case 'code':
      if (input.filePath) {
        chips.push({ kind: 'file', label: basename(input.filePath), value: `file ${input.filePath}` });
      }
      break;
    default:
      break;
  }
  return chips;
}

/** `[Context: scene scenes/main.scene.json, selected entity 'player']` or null. */
export function contextBlock(chips: readonly ContextChip[]): string | null {
  if (!chips.length) return null;
  return `[Context: ${chips.map((c) => c.value).join(', ')}]`;
}

/** Prefix a message with its context block. */
export function withContext(text: string, chips: readonly ContextChip[]): string {
  const block = contextBlock(chips);
  return block ? `${block}\n${text}` : text;
}

/** Split a stored message back into its context line and the user's text. */
export function splitContext(text: string): { context: string | null; body: string } {
  const match = /^\[Context: ([^\n]*)\]\n?/.exec(text);
  if (!match) return { context: null, body: text };
  return { context: match[1] ?? null, body: text.slice(match[0].length) };
}

export function basename(path: string): string {
  const clean = path.replace(/\\/g, '/');
  return clean.slice(clean.lastIndexOf('/') + 1);
}

export type ModelAction = 'generate' | 'refine' | 'detail' | 'lowpoly' | 'stylized' | 'custom';

/** Art style a model request targets (`auto` = match the project's existing models). */
export type ArtStyle = 'auto' | 'low-poly' | 'stylized' | 'realistic' | 'voxel' | 'pixel';

export const ART_STYLES: ReadonlyArray<{ value: ArtStyle; label: string }> = [
  { value: 'auto', label: 'Project style' },
  { value: 'low-poly', label: 'Low-poly' },
  { value: 'stylized', label: 'Stylized' },
  { value: 'realistic', label: 'Realistic' },
  { value: 'voxel', label: 'Voxel' },
  { value: 'pixel', label: 'Pixel' },
];

/** One line telling the modeler which art style to build in. */
export function artStyleLine(style: ArtStyle | null | undefined): string {
  switch (style ?? 'auto') {
    case 'low-poly':
      return 'Art style: low-poly. Few segments (6-12 on curves), crisp facets ("flatShade" as the last op), flat color materials, no or very subtle procedural textures; proportions may be simplified and chunky.';
    case 'stylized':
      return 'Art style: stylized. Exaggerated, readable proportions (bigger heads, hands, props), soft bevels, a cohesive saturated palette, simple or painterly textures; sizes follow the game, not real-world measurements.';
    case 'realistic':
      return 'Art style: realistic. Real-world dimensions and proportions (look them up), PBR material values and procedural textures on every large surface, beveled edges, wear and variation.';
    case 'voxel':
      return 'Art style: voxel. Build from equal-sized cubes on a grid (box parts and `array`), no bevels or smoothing ("flatShade"), flat colors per voxel material, no procedural textures.';
    case 'pixel':
      return 'Art style: pixel art. Simple blocky forms, flat-shaded, low-resolution textures (texture resolution 32-64 or `.pixel.json` image maps), limited palette, unlit or toon materials.';
    default:
      return "Art style: match the project. Look at the existing models in models/ (and any art direction in AGENTS.md or the project's docs) and build in the same style and scale; if there is nothing to go on, ask the user which style they want (low-poly, stylized, realistic, voxel or pixel) instead of assuming realistic.";
  }
}

/** Project AI settings read by every agent run (must match the worker's ai-settings.ts). */
export const AI_SETTINGS_FILE = '.prism/ai-settings.json';

/** Whether the modeler should build the mesh with local AI generation (Model tab checkbox). */
export function localGenLine(action: ModelAction, localGen: boolean): string {
  if (!localGen)
    return 'Local generation is OFF for this request: do not call prism_model_generate; build the model from parts with the prism.model/1 tools.';
  return action === 'generate' || action === 'custom'
    ? 'Local generation is ON: create the base mesh with prism_model_generate (SDXL concept image, then the ComfyUI image-to-3D workflow) using a detailed visual prompt and the target file as `out`. It runs as a job and may wait in a queue behind other projects\' generations (one runs at a time); keep calling it with {"job": id} until it is done, then refine the result with prism_model_edit.'
    : 'Local generation is ON: if this change needs a new mesh, create it with prism_model_generate (SDXL + ComfyUI, one generation at a time, queued) and refine it with prism_model_edit; small changes can be edited directly.';
}

/**
 * A well-structured modeling request for the Assistant: what to do, which file
 * and part, and how to check the result.
 */
export function modelRequest(options: {
  action: ModelAction;
  prompt: string;
  modelPath: string | null;
  partPath: string | null;
  /** Target art style (default: match the project). */
  style?: ArtStyle | null;
  /** Build new meshes with local generation (SDXL + ComfyUI) instead of by hand. */
  localGen?: boolean;
}): string {
  const { action, prompt, modelPath, partPath, style, localGen = false } = options;
  const target = modelPath ?? 'models/<name>.model.json (pick a short snake_case name)';
  const lines: string[] = [];
  const ask = prompt.trim();
  switch (action) {
    case 'generate':
      lines.push(
        `Create a new 3D model${ask ? `: ${ask}` : ''}.`,
        `Write it to ${modelPath && !ask ? modelPath : 'a new file models/<name>.model.json (pick a short snake_case name)'}.`,
      );
      break;
    case 'refine':
      lines.push(
        `Refine the part '${partPath ?? '(whole model)'}' in ${target}${ask ? `: ${ask}` : ' so it looks more polished and better proportioned'}.`,
        'Keep the other parts unchanged unless they must move to fit.',
      );
      break;
    case 'detail':
      lines.push(
        `Add detail to ${partPath ? `the part '${partPath}' of ` : ''}${target}${ask ? `: ${ask}` : ' (secondary shapes, bevels, trims, small props) without changing its silhouette'}.`,
      );
      break;
    case 'lowpoly':
      lines.push(
        `Make ${partPath ? `the part '${partPath}' of ` : ''}${target} low-poly${ask ? `: ${ask}` : ''}: reduce segment counts (6-12), add "flatShade" as the last op, and keep the silhouette readable.`,
      );
      break;
    case 'stylized':
      lines.push(
        `Make ${partPath ? `the part '${partPath}' of ` : ''}${target} stylized${ask ? `: ${ask}` : ''}: exaggerate proportions, use a cohesive saturated material palette, soften with bevels and gentle noise.`,
      );
      break;
    default:
      lines.push(ask || 'Improve this model.');
      lines.push(`File: ${target}${partPath ? `, selected part '${partPath}'` : ''}.`);
      break;
  }
  lines.push(
    'Follow the prism.model/1 schema and its Modeling guide (prism_docs / AGENTS.md): a tree of named parts, each a shape + ops + transform; define every material you reference; Y is up, front faces -Z, meters, degrees.',
    action === 'lowpoly' || action === 'stylized' ? '' : artStyleLine(style),
    localGenLine(action, localGen),
    'Work in passes, quality over speed: plan the size in the game\'s scale (meters), the 3-6 big forms, materials and how the art style shows in each; block out the big forms; then add secondary forms (bevels, trims, joints) and finally details. Reuse the project\'s texture presets (textures/presets/*.texpreset.json) for materials when they fit.',
    `After every pass, inspect the 6-view sheet (prism_model_render, or \`prism model render ${modelPath ?? '<file>'} --out .prism/preview.png --views sheet\`) and use \`part\` close-ups for small parts. Prefer prism_model_edit to change parts by path. For people, animals and creatures use one "sculpt" part (never stacks of capsules); for humans start from prism_model_template name "human" and customize it.`,
    'Critique each render against the target style: silhouette and proportions, gaps, floating or buried parts, scale that does not fit the game, unintended faceting (or missing facets in a faceted style). Fix and re-render until nothing is left to fix; never stop after a single render.',
  );
  return lines.filter(Boolean).join('\n');
}

/** Request to fix a runtime error seen in Play mode. */
export function fixErrorRequest(options: { scenePath: string | null; errors: readonly string[] }): string {
  const log = options.errors.slice(-12).join('\n');
  return [
    `The game reported errors while running${options.scenePath ? ` scene ${options.scenePath}` : ''} in Play mode. Find the cause and fix it.`,
    '```',
    log,
    '```',
    'Check scripts (*.rhai), scene components and asset paths. Run `prism check .` afterwards to validate the project.',
  ].join('\n');
}

/* ------------------------------------------------------------------ *
 * Sound tab
 * ------------------------------------------------------------------ */

type JsonObject = Record<string, unknown>;
const isJsonObject = (v: unknown): v is JsonObject => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Strip characters that would break the one-line `[Context: ...]` block. */
function oneLine(text: string, max: number): string {
  const clean = text.replace(/[\r\n\]]+/g, ' ').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/**
 * One-line outline of a sound document for the AI context: kind, duration,
 * layers (name:source), instruments, tracks, patterns, voice.
 * e.g. `sfx 0.6s; layers body:osc, click:noise; master fx eq,compressor`.
 */
export function soundDocSummary(doc: unknown): string {
  if (!isJsonObject(doc)) return '';
  const parts: string[] = [String(doc.kind ?? 'sfx')];
  if (typeof doc.duration === 'number') parts[0] += ` ${doc.duration}s`;
  const named = (list: unknown, label: (item: JsonObject, i: number) => string) =>
    Array.isArray(list) ? list.filter(isJsonObject).map(label).slice(0, 12).join(', ') : '';
  const layers = named(doc.layers, (l, i) => {
    const source = isJsonObject(l.source) ? String(l.source.type ?? '?') : '?';
    return `${typeof l.name === 'string' ? l.name : `#${i}`}:${source}`;
  });
  if (layers) parts.push(`layers ${layers}`);
  if (isJsonObject(doc.instruments) && Object.keys(doc.instruments).length) {
    parts.push(
      `instruments ${Object.entries(doc.instruments)
        .map(([k, v]) => `${k}:${isJsonObject(v) ? String(v.type ?? '?') : '?'}`)
        .join(', ')}`,
    );
  }
  const tracks = named(doc.tracks, (t, i) => `${typeof t.name === 'string' ? t.name : `#${i}`}`);
  if (tracks) parts.push(`tracks ${tracks}${typeof doc.tempo === 'number' ? ` @${doc.tempo}bpm` : ''}`);
  if (isJsonObject(doc.patterns) && Object.keys(doc.patterns).length)
    parts.push(`patterns ${Object.keys(doc.patterns).slice(0, 12).join(', ')}`);
  if (isJsonObject(doc.voice)) {
    const lines = Array.isArray(doc.voice.lines) ? doc.voice.lines.length : 0;
    parts.push(`voice ${String(doc.voice.mode ?? 'speech')} ${String(doc.voice.voice ?? '')} ${lines} lines`.trim());
  }
  if (isJsonObject(doc.master) && Array.isArray(doc.master.effects) && doc.master.effects.length)
    parts.push(
      `master fx ${doc.master.effects
        .filter(isJsonObject)
        .map((e) => String(e.type))
        .join(',')}`,
    );
  return oneLine(parts.join('; '), 400);
}

/**
 * One-line summary of a render analysis: every top-level number (rounded),
 * nested objects one level deep, long arrays as their count.
 */
export function soundAnalysisLine(analysis: unknown): string {
  if (!isJsonObject(analysis)) return '';
  const round = (n: number) => (Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 100) / 100);
  const items: string[] = [];
  for (const [key, value] of Object.entries(analysis)) {
    if (typeof value === 'number' && Number.isFinite(value)) items.push(`${key} ${round(value)}`);
    else if (typeof value === 'string' && value.length < 40) items.push(`${key} ${value}`);
    else if (typeof value === 'boolean') items.push(`${key} ${value}`);
    else if (isJsonObject(value)) {
      const inner = Object.entries(value)
        .filter(([, v]) => typeof v === 'number' && Number.isFinite(v))
        .map(([k, v]) => `${k} ${round(v as number)}`);
      if (inner.length) items.push(`${key} {${inner.slice(0, 8).join(', ')}}`);
    }
  }
  return oneLine(items.join(', '), 500);
}

export type SoundAction = 'generate' | 'refine' | 'variations' | 'fix' | 'custom';

/**
 * A well-structured sound request for the Assistant (Sound tab prompt bar):
 * what to make, which file/element, and how to check it without hearing it.
 */
export function soundRequest(options: {
  action: SoundAction;
  prompt: string;
  soundPath: string | null;
  selection?: string | null;
  /** Short analysis line of the last render (see soundAnalysisLine). */
  analysis?: string | null;
}): string {
  const { action, prompt, soundPath, selection, analysis } = options;
  const ask = prompt.trim();
  const target = soundPath ?? 'sounds/<name>.sound.json (pick a short snake_case name)';
  const lines: string[] = [];
  switch (action) {
    case 'generate':
      lines.push(`Create a new sound${ask ? `: ${ask}` : ''}.`, `Write it to ${soundPath && !ask ? soundPath : 'a new file sounds/<name>.sound.json (pick a short snake_case name)'}.`);
      break;
    case 'refine':
      lines.push(
        `Refine ${selection ? `'${selection}' in ` : ''}${target}${ask ? `: ${ask}` : ' so it sounds more polished and fits a game mix'}.`,
        'Keep the other layers/tracks unchanged unless they must change to fit.',
      );
      break;
    case 'variations':
      lines.push(
        `Add variations to ${target}${ask ? `: ${ask}` : ''}: use "variables" plus "variants" (different seed and small pitch/timing/tone changes) so repeated plays do not sound identical.`,
      );
      break;
    case 'fix':
      lines.push(`Fix the problems in ${target}${ask ? `: ${ask}` : ' shown by the last render analysis (clipping, loudness, muddiness, harshness, too long a tail)'}.`);
      break;
    default:
      lines.push(ask || 'Improve this sound.');
      lines.push(`File: ${target}${selection ? `, selected element '${selection}'` : ''}.`);
      break;
  }
  if (analysis) lines.push(`Last render analysis: ${analysis}.`);
  lines.push(
    'Follow the prism.sound/1 format and the Sound design guide (prism_docs / AGENTS.md); start from a close preset when one fits. Times in seconds (pattern times in beats), gains in dB, pitches as MIDI numbers or note names.',
    'You cannot hear the result: after every change run prism_sound_render (or use prism_sound_edit, which renders) and read the analysis numbers and the spectrogram/waveform images against the targets in the guide (duration, peak <= -1 dBFS, loudness, spectral centroid, onsets/pitch).',
    'Prefer prism_sound_edit with JSON paths (e.g. "layers[1].source.freq", "tracks.Piano.clips[0]") over rewriting the file. Iterate until the analysis and images match the intent; never stop after a single render.',
  );
  return lines.filter(Boolean).join('\n');
}
